import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import AlreadySignedUpDialog from '../../components/AlreadySignedUpDialog'
import Button from '../../components/Button'
import Heading from '../../components/Heading'
import Input from '../../components/Input'
import PageLayout from '../../components/PageLayout'
import { trackCompleteRegistrationEvent } from '../../helpers/facebookTracking'
import {
  getPasswordFromSessionStorage,
  removePasswordFromSessionStorage,
} from '../../helpers/sessionStorage'
import { signUp } from '../../helpers/supabase'
import { validateEmail } from '../../helpers/validations'

export default function EmailPage() {
  const [email, setEmail] = useState('')
  const [emailValid, setEmailValid] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [password, setPassword] = useState('')
  const [openAlreadySignedUpDialog, setOpenAlreadySignedUpDialog] =
    useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const isEmailValid = validateEmail(email)

  const doSignUp = async () => {
    setIsSubmitting(true)

    const { error } = await signUp({ email, password })

    if (error) {
      if (error.message === 'User already registered') {
        setOpenAlreadySignedUpDialog(true)
      } else {
        console.error(error)
      }

      setIsSubmitting(false)
      setShowConfirm(false)

      return
    }

    removePasswordFromSessionStorage()

    await trackCompleteRegistrationEvent()

    navigate('/sign-up/preparing')
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return

    if (!isEmailValid) {
      setEmailValid(false)
      return
    }

    setEmailValid(true)
    setShowConfirm(true)
  }

  useEffect(() => {
    const passwordFromSessionStorage = getPasswordFromSessionStorage()

    if (passwordFromSessionStorage) {
      setPassword(passwordFromSessionStorage)
    } else {
      navigate('/sign-up/password')
    }
  }, [navigate])

  if (showConfirm) {
    return (
      <PageLayout>
        <div className="shrink-0">
          <Heading
            title={t('components.emailPreSignupConfirm.title')}
            subtitle={t('components.emailPreSignupConfirm.subtitle')}
          />
        </div>

        <div className="flex grow flex-col justify-between">
          <div className="flex grow flex-col items-center justify-center">
            <p
              className="text-center text-xl font-semibold break-all"
              data-csr-mask
            >
              {email}
            </p>
          </div>

          <div className="space-y-3">
            <Button
              disabled={isSubmitting}
              submitting={isSubmitting}
              onClick={doSignUp}
              title={t('components.emailPreSignupConfirm.confirm')}
            />
            <button
              className="text-custom-light-gray w-full py-2 text-sm transition-colors hover:opacity-70"
              disabled={isSubmitting}
              onClick={() => setShowConfirm(false)}
              type="button"
            >
              {t('components.emailPreSignupConfirm.edit')}
            </button>
          </div>
        </div>

        <AlreadySignedUpDialog
          open={openAlreadySignedUpDialog}
          onClose={() => setOpenAlreadySignedUpDialog(false)}
        />
      </PageLayout>
    )
  }

  return (
    <>
      <PageLayout>
        <div className="shrink-0">
          <Heading
            title={t('pages.signUp.email.heading')}
            subtitle={t('pages.signUp.email.subheading')}
          />
        </div>

        <form
          className="flex grow flex-col justify-between"
          noValidate
          onSubmit={handleSubmit}
        >
          <div className="flex grow flex-col items-center justify-center">
            <div className="w-full max-w-[338px] space-y-3">
              <Input
                autoComplete="email"
                autoFocus
                value={email}
                invalid={!emailValid}
                name="email"
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('shared.email')}
                type="email"
                dataTestId="textfield-email"
                bgColor={'#FFFFFF'}
              />
              <p
                className={
                  emailValid
                    ? 'text-sm text-[#a1a1a1]'
                    : 'text-custom-error text-sm'
                }
              >
                {emailValid ? (
                  <Trans
                    components={{
                      privacyPolicyLink: (
                        <Link
                          className="underline"
                          target="_blank"
                          to="https://app-studio.ai/privacy"
                        />
                      ),
                      termsAndConditionsLink: (
                        <Link
                          className="underline"
                          target="_blank"
                          to="https://app-studio.ai/terms"
                        />
                      ),
                    }}
                    i18nKey="pages.signUp.email.text"
                  />
                ) : (
                  t('pages.signUp.email.emailNotValid')
                )}
              </p>
            </div>
          </div>

          <Button
            disabled={email.length < 1}
            submitting={isSubmitting}
            type="submit"
            title={t('shared.continue')}
            dataTestId="button-continue-credentials"
          />
        </form>
      </PageLayout>

      <AlreadySignedUpDialog
        open={openAlreadySignedUpDialog}
        onClose={() => setOpenAlreadySignedUpDialog(false)}
      />
    </>
  )
}
